import apiClient from './apiClient';

export type RiskCategory = 'financial' | 'operational' | 'regulatory' | 'safety' | 'weather' | 'other';
export type RiskStatus = 'open' | 'mitigating' | 'accepted' | 'closed';
export type MitigationStatus = 'planned' | 'in_progress' | 'done' | 'cancelled';

export interface RiskMitigationPlan {
  id: string;
  riskId: string;
  description: string;
  actionOwnerUserId: string | null;
  dueDate: string | null;
  status: MitigationStatus;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RiskRegisterRow {
  id: string;
  projectId: string;
  riskCode: string;
  title: string;
  description: string | null;
  category: RiskCategory;
  probability: number;
  impact: number;
  riskScore: number;
  status: RiskStatus;
  ownerUserId: string | null;
  identifiedAt: string;
  closedAt: string | null;
  residualProbability: number | null;
  residualImpact: number | null;
  residualScore: number | null;
  mitigationPlans?: RiskMitigationPlan[];
  createdAt: string;
  updatedAt: string;
}

export interface RiskHeatMapCell {
  probability: number;
  impact: number;
  riskScore: number;
  count: number;
}

export interface RiskPayload {
  projectId: string;
  riskCode: string;
  title: string;
  description?: string | null;
  category: RiskCategory;
  probability: number;
  impact: number;
  status?: RiskStatus;
  ownerUserId?: string | null;
  residualProbability?: number | null;
  residualImpact?: number | null;
}

class RisksService {
  async list(params: { projectId?: string; status?: RiskStatus; category?: RiskCategory; probability?: number; impact?: number } = {}): Promise<RiskRegisterRow[]> {
    const response: any = await apiClient.get('/risks', { params });
    return response.data;
  }

  async create(payload: RiskPayload): Promise<RiskRegisterRow> {
    const response: any = await apiClient.post('/risks', payload);
    return response.data;
  }

  async update(id: string, payload: Partial<RiskPayload>): Promise<RiskRegisterRow> {
    const response: any = await apiClient.patch(`/risks/${id}`, payload);
    return response.data;
  }

  async close(id: string): Promise<RiskRegisterRow> {
    const response: any = await apiClient.post(`/risks/${id}/close`);
    return response.data;
  }

  async heatMap(projectId: string): Promise<RiskHeatMapCell[]> {
    const response: any = await apiClient.get('/risks/heat-map', { params: { projectId } });
    return response.data;
  }

  async top(projectId: string, limit: number = 10): Promise<RiskRegisterRow[]> {
    const response: any = await apiClient.get('/risks/top', { params: { projectId, limit } });
    return response.data;
  }

  async addMitigationPlan(riskId: string, payload: { description: string; dueDate?: string | null; status?: MitigationStatus }): Promise<RiskMitigationPlan> {
    const response: any = await apiClient.post(`/risks/${riskId}/mitigation-plans`, payload);
    return response.data;
  }

  async updateMitigationPlan(planId: string, payload: Partial<{ description: string; dueDate: string | null; status: MitigationStatus }>): Promise<RiskMitigationPlan> {
    const response: any = await apiClient.patch(`/risks/mitigation-plans/${planId}`, payload);
    return response.data;
  }

  async deleteMitigationPlan(planId: string): Promise<void> {
    await apiClient.delete(`/risks/mitigation-plans/${planId}`);
  }
}

export default new RisksService();
